import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCallback, useEffect, useRef, useState } from "react";
import PublicationsApi from "../../api/publicationsApi";
import CircleAvatar from "../../components/CircleAvatar/CircleAvatar";
import MultipleSelect from "../../components/MultipleSelect/MultipleSelect";
import { Button } from "../../components/ui/button";
import {
  Publication,
  PublicationFilterScope,
  PublicationSort,
} from "../../types/Publications";
import AllPublications from "./AllPublications";
import PublicationInspector, {
  PublicationDecisionOutcome,
} from "./PublicationInspector";
import {
  formatRequestDate,
  getFallbackTotal,
  getUserLabel,
} from "./publicationQueueModel";
import s from "./Publications.module.css";

const DEFAULT_PAGE_SIZE = 10;

const Publications = () => {
  const token = localStorage.getItem("token");
  const [activeTab, setActiveTab] = useState<"queue" | "history">("queue");
  const [publications, setPublications] = useState<Publication[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [queueSelectedId, setQueueSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decisionNotice, setDecisionNotice] = useState<{
    message: string;
    tone: "success" | "error";
  } | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const requestIdRef = useRef(0);

  const loadQueue = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!token) {
      setPublications([]);
      setTotal(0);
      setQueueSelectedId(null);
      setError(
        "Your admin session is unavailable. Sign in again to load Publications.",
      );
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await PublicationsApi.getFilteredPublications(token, {
        scope: PublicationFilterScope.ALL,
        page: page + 1,
        limit: pageSize,
        isApproveRequested: true,
        deleted: false,
        sort: PublicationSort.APPROVE_REQUESTED_AT_DESC,
      });
      if (!response.status) {
        throw new Error(
          response.message || "Unable to load Publication review queue.",
        );
      }
      if (requestId !== requestIdRef.current) return;

      const nextPublications = response.publications ?? [];
      setPublications(nextPublications);
      setTotal(
        getFallbackTotal(
          page,
          pageSize,
          nextPublications.length,
          response.totalAmount,
        ),
      );
      setQueueSelectedId((current) =>
        nextPublications.some(({ _id }) => _id === current)
          ? current
          : (nextPublications[0]?._id ?? null),
      );
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error loading Publication review queue:", loadError);
      setPublications([]);
      setTotal(0);
      setQueueSelectedId(null);
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Unable to load Publication review queue.",
      );
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [page, pageSize, token]);

  useEffect(() => {
    if (activeTab !== "queue") {
      requestIdRef.current += 1;
      return;
    }
    loadQueue();
    return () => {
      requestIdRef.current += 1;
    };
  }, [activeTab, loadQueue, retryVersion]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const handleDecisionResolved = useCallback(
    (outcome: PublicationDecisionOutcome) => {
      setDecisionNotice({ message: outcome.message, tone: outcome.tone });
      if (
        outcome.publication.isApproveRequested &&
        !outcome.publication.deleted
      )
        return;
      setPublications((current) => {
        const resolvedIndex = current.findIndex(
          ({ _id }) => _id === outcome.publication._id,
        );
        const remaining = current.filter(
          ({ _id }) => _id !== outcome.publication._id,
        );
        setQueueSelectedId((selected) =>
          selected !== outcome.publication._id
            ? selected
            : (remaining[
                Math.min(Math.max(resolvedIndex, 0), remaining.length - 1)
              ]?._id ?? null),
        );
        return remaining;
      });
      setTotal((current) => Math.max(0, current - 1));
    },
    [],
  );

  const handleTabChange = (nextTab: "queue" | "history") => {
    setActiveTab(nextTab);
    if (nextTab === "history") {
      setQueueSelectedId(null);
    }
  };

  return (
    <main className={s.page} aria-labelledby="publications-heading">
      <header className={s.header}>
        <div>
          <h1 id="publications-heading" className={s.title}>
            Publications
          </h1>
          <p className={s.subtitle}>
            {activeTab === "queue"
              ? "Review submitted Publications in request order."
              : "Search and inspect the complete Publication lifecycle."}
          </p>
        </div>
        {activeTab === "queue" && (
          <div className={s.queueCount} aria-live="polite">
            {total} pending
          </div>
        )}
      </header>

      <div
        className={s.tabs}
        role="tablist"
        aria-label="Publication workspaces"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "queue"}
          aria-controls="publication-review-queue"
          className={activeTab === "queue" ? s.activeTab : s.tab}
          onClick={() => handleTabChange("queue")}
        >
          Review queue
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "history"}
          aria-controls="all-publications"
          className={activeTab === "history" ? s.activeTab : s.tab}
          onClick={() => handleTabChange("history")}
        >
          All Publications
        </button>
      </div>

      {decisionNotice && (
        <div
          className={`${s.queueNotice} ${decisionNotice.tone === "success" ? s.successNotice : s.errorNotice}`}
          role={decisionNotice.tone === "success" ? "status" : "alert"}
        >
          <span>{decisionNotice.message}</span>
          <button type="button" onClick={() => setDecisionNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      {activeTab === "queue" ? (
        <section
          id="publication-review-queue"
          role="tabpanel"
          aria-label="Review queue"
          className={s.queuePanel}
        >
          <div className={s.workspace}>
            <div className={s.queueColumn}>
              {isLoading ? (
                <div className={s.state} role="status">
                  Loading Publication review queue…
                </div>
              ) : error ? (
                <div className={s.state} role="alert">
                  <strong>Review queue could not be loaded</strong>
                  <span>{error}</span>
                  <Button
                    type="button"
                    onClick={() => setRetryVersion((value) => value + 1)}
                  >
                    <RefreshIcon aria-hidden="true" /> Retry
                  </Button>
                </div>
              ) : publications.length === 0 ? (
                <div className={s.state} role="status">
                  <strong>Review queue is empty</strong>
                  <span>No Publications are awaiting review.</span>
                </div>
              ) : (
                <div className={s.list} aria-label="Pending Publications">
                  {publications.map((publication) => {
                    const creatorId =
                      typeof publication.createdBy === "string"
                        ? publication.createdBy
                        : publication.createdBy?._id;
                    const isSelected = queueSelectedId === publication._id;
                    return (
                      <button
                        type="button"
                        key={publication._id}
                        className={`${s.queueItem} ${isSelected ? s.selectedItem : ""}`}
                        aria-pressed={isSelected}
                        onClick={() => setQueueSelectedId(publication._id)}
                      >
                        <div className={s.queueItemMain}>
                          <CircleAvatar
                            userId={creatorId}
                            width="36px"
                            height="36px"
                            fontSize="12px"
                            borderRadius="50%"
                            hideNoAvtaarBlock={false}
                          />
                          <div className={s.queueText}>
                            <strong>
                              {publication.title || "Untitled Publication"}
                            </strong>
                            <span className={s.queueAuthor}>
                              {getUserLabel(publication.createdBy)}
                            </span>
                            <time
                              className={s.queueDate}
                              dateTime={publication.approveRequestedAt}
                            >
                              {formatRequestDate(
                                publication.approveRequestedAt,
                              )}
                            </time>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <footer
                className={s.pagination}
                aria-label="Review queue pagination"
              >
                <div className={s.pageSizeField}>
                  <MultipleSelect
                    data={[10, 20, 50].map((size) => ({
                      value: String(size),
                      lable: String(size),
                    }))}
                    value={String(pageSize)}
                    setValue={(value) => {
                      setPageSize(Number(value));
                      setPage(0);
                      setQueueSelectedId(null);
                    }}
                    multiplie={false}
                    disabled={isLoading}
                    lable="Rows per page"
                  />
                </div>
                <span>
                  Page {page + 1} of {pageCount}
                </span>
                <div className={s.paginationActions}>
                  <Button
                    type="button"
                    variant="outline"
                    aria-label="Previous page"
                    disabled={isLoading || page === 0}
                    onClick={() => {
                      setQueueSelectedId(null);
                      setPage((value) => Math.max(0, value - 1));
                    }}
                  >
                    <NavigateBeforeIcon aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    aria-label="Next page"
                    disabled={isLoading || page + 1 >= pageCount}
                    onClick={() => {
                      setQueueSelectedId(null);
                      setPage((value) => value + 1);
                    }}
                  >
                    <NavigateNextIcon aria-hidden="true" />
                  </Button>
                </div>
              </footer>
            </div>
            <PublicationInspector
              publicationId={queueSelectedId}
              token={token}
              onDecisionResolved={handleDecisionResolved}
            />
          </div>
        </section>
      ) : (
        <section
          id="all-publications"
          role="tabpanel"
          aria-label="All Publications"
          className={s.queuePanel}
        >
          <AllPublications token={token} />
        </section>
      )}
    </main>
  );
};

export default Publications;
